import { datadogRum } from '@datadog/browser-rum';
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from './utils/function';
import { isEmbeddedMode } from './utils/ltiEmbedded';

// Cached snapshot of session-derived values to avoid hitting localStorage
// (and parsing JSON / cookies via getLocalStorage) on every render-path call.
// Cleared by setProfileCookies/deleteProfileCookies via clearSessionCache().
let cachedSession = null;

const readSession = () => ({
  userName: getLocalStorage('user.name'),
  userId: getLocalStorage('userId'),
  userRole: getLocalStorage('jeddaiRole'),
  authToken: shouldPersistJwtToken() ? localStorage.getItem('jwtToken') : null,
});

const getSession = () => {
  if (cachedSession === null) {
    cachedSession = readSession();
  }
  return cachedSession;
};

const clearSessionCache = () => {
  cachedSession = null;
};

export const getUserName = () => getSession().userName;
export const getUserId = () => getSession().userId;
export const getUserRole = () => getSession().userRole; // Updated to use jeddaiRole

const isDevEnv =
  (process.env.REACT_APP_ENV ?? '').toLowerCase() === 'dev' ||
  (process.env.NODE_ENV ?? '').toLowerCase() === 'development';
const forceLocalJwt =
  isDevEnv &&
  (process.env.REACT_APP_FORCE_LOCAL_JWT ?? '').toLowerCase() === 'true';

const getHostFromUrl = (url) => {
  try {
    return new URL(url, window.location.origin).hostname;
  } catch {
    return null;
  }
};

const isCookieCompatibleHost = (host) => {
  if (!host) {
    return false;
  }
  return (
    host.endsWith('.jeddle.com') ||
    host.endsWith('.jeddle.com.au') ||
    host.endsWith('.jeddle.duxdigital.net')
  );
};

export const shouldPersistJwtToken = () => {
  if (forceLocalJwt) {
    return true;
  }

  // Embedded in an LMS iframe: cookies (even SameSite=None) are unreliable when
  // 3rd-party cookies are blocked, so the JWT MUST live in localStorage. (PLAN §8.3)
  if (isEmbeddedMode()) {
    return true;
  }

  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL ?? window.location.origin;
  const apiHost = getHostFromUrl(apiBaseUrl);
  const appHost = window.location.hostname;

  return !(isCookieCompatibleHost(apiHost) && isCookieCompatibleHost(appHost));
};

export const getAuthToken = () => getSession().authToken;

// Returns the token's `exp` claim in epoch seconds, or null when the token is
// not a decodable JWT or carries no numeric exp. Callers must treat null as
// "cannot prove staleness", NOT as expired — a decode quirk must never lock a
// working session out (the server's 401 remains the authority for those).
const decodeJwtExp = (token) => {
  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
};

/**
 * True only when the session runs in localStorage-JWT mode AND the stored
 * token is PROVABLY past its exp. The backend JWT expires after ~8h but the
 * client never used to check — a returning user's stale token was presented
 * as valid, the dashboard prefetch burst all 401'd, and service.js wiped
 * storage mid-session with a hard redirect. Cookie-auth hosts always return
 * false: they have no localStorage JWT and the server validates the cookie.
 */
export const isStoredJwtExpired = () => {
  if (!shouldPersistJwtToken()) {
    return false;
  }
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    return false; // absent ≠ provably expired; existing flows handle it
  }
  const exp = decodeJwtExp(token);
  if (exp === null) {
    return false; // undecodable — defer to the server rather than risk lockout
  }
  return Date.now() >= exp * 1000;
};

export const setAuthToken = (token) => {
  if (shouldPersistJwtToken()) {
    localStorage.setItem('jwtToken', token);
  } else {
    localStorage.removeItem('jwtToken');
  }
  clearSessionCache();
};

export const getCookie = (name) => {
  const cookieValue = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`));

  return cookieValue ? cookieValue.split('=')[1] : null;
};

export const setCookie = (key, value) => {
  const expiry = 30 * 24 * 60 * 60 * 1000;
  const expires = expiry
    ? `; expires=${new Date(Date.now() + expiry).toUTCString()}`
    : '';
  document.cookie = `${key}=${value}${expires}; path=/`;
};

export const setProfileCookies = (profile) => {
  console.log('Setting profile cookies for user:', profile);
  if (profile.token) {
    setAuthToken(profile.token);
  }

  setLocalStorage('user.name', profile.name);
  setLocalStorage('userId', profile.userId);
  
  // Store jeddaiRole as primary role system
  const jeddaiRole = profile?.permissions?.jeddaiRole || profile.role;
  setLocalStorage('jeddaiRole', jeddaiRole);
  
  // Keep old role for backward compatibility during transition
  setLocalStorage('role', profile.role);

  if (profile.year !== undefined) {
    setLocalStorage('year', profile.year);
  }
  // Always sync both state and states to localStorage
  if (Array.isArray(profile.states) && profile.states.length > 0) {
    setLocalStorage('states', profile.states);
    setLocalStorage('state', profile.states[0]);
  } else if (profile.state !== undefined) {
    setLocalStorage('state', profile.state);
    setLocalStorage('states', profile.state ? [profile.state] : []);
  }

  if (profile.classes) {
    localStorage.setItem('classes', JSON.stringify(profile.classes));
    setLocalStorage('classes', profile.classes);
  }

  datadogRum.setUser({
    id: profile.userId,
    name: profile.name,
    email: profile.email,
    roles: jeddaiRole, // Updated to use jeddaiRole
  });
  datadogRum.setUserProperty('user.role', jeddaiRole); // Updated to use jeddaiRole

  clearSessionCache();
};

export const deleteCookie = (key) => {
  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

export const deleteProfileCookies = () => {
  localStorage.removeItem('jwtToken');
  deleteCookie('user.name');
  deleteCookie('userId');
  deleteCookie('role');
  deleteCookie('jeddaiRole'); // Added jeddaiRole cleanup
  deleteCookie('state');
  deleteCookie('year');
  deleteCookie('classes');
  deleteLocalStorage('user.name');
  deleteLocalStorage('userId');
  deleteLocalStorage('role');
  deleteLocalStorage('jeddaiRole'); // Added jeddaiRole cleanup
  deleteLocalStorage('states');
  deleteLocalStorage('state');
  deleteLocalStorage('year');
  deleteLocalStorage('classes');
  deleteLocalStorage('showNotificationBar');

  clearSessionCache();
};
